/**
 * Los textos que se ven en el armazón, la sesión y la memoria. Un solo sitio:
 * el nombre del programa y cada frase de estas pantallas se cambian aquí, no
 * en el JSX.
 *
 * Los textos de cada sección viven junto a ella (`work/<sección>/texts.ts`,
 * `chat/texts.ts`, `usage/texts.ts`, `alfred/texts.ts`).
 */

/** El nombre del programa, como se dice en pantalla. */
export const APP_NAME = "Conti Club Más";

/** La inicial del distintivo de la marca (`BrandName`). */
export const APP_INITIAL = "C";

export const SESSION_TEXTS = {
  entering: "Entrando…",
  signInFailed: "No se pudo iniciar sesión",
  sessionExpired: "Tu sesión caducó",
  signInAgain: "Volver a entrar",
  signOut: "Cerrar sesión",
  signingOut: "Cerrando sesión…",
  unreachableTitle: "No pudimos conectar con el servidor",
  unreachableHint:
    "Puede ser la conexión o que el servidor no esté disponible ahora. Prueba otra vez en un momento.",
  reference: "Referencia:",
  retry: "Reintentar",
  goToSignIn: "Ir al inicio de sesión",
  accessDeniedTitle: `No tienes acceso a ${APP_NAME}`,
  noEmail: "una cuenta sin correo",
  signedInAs: "Has entrado como",
  accessDeniedReason:
    "y esa cuenta no tiene permiso para usar esta aplicación. Si crees que debería, pídeselo a quien administra el programa; si no es tu cuenta de trabajo, sal y entra con otra.",
} as const;

export const SHELL_TEXTS = {
  workspaceOutside: "useWorkspace fuera del armazón de la aplicación",
  dashboardPeriodOutside: "useDashboardPeriod fuera del panel",
  sessionOutside: "useSession fuera de SessionProvider",
  leaveConversationTitle: "¿Dejamos la conversación de ahora?",
  leaveWaitingForAnswer:
    "El asistente está esperando una respuesta tuya y no seguirá hasta tenerla. La conversación no se pierde --queda en el historial--, pero lo que está a medias se queda ahí.",
  leaveTurnRunning:
    "Hay un turno en marcha. La conversación no se pierde --queda en el historial--, pero lo que llegue después no lo verás aquí.",
  stayHere: "Seguir donde estaba",
  askInNew: "Preguntar en una nueva",
  showPanel: "Mostrar el panel",
  hidePanel: "Ocultar el panel",
  settings: "Ajustes",
  workModeLabel: "Modo de trabajo",
  periodPicker: "Mes o año",
} as const;

export const SETTINGS_TEXTS = {
  title: "Ajustes",
  memoryHint:
    "Lo que el asistente recuerda se lee y se corrige en Chat ▸ Memoria.",
  theme: "Tema",
  session: "Sesión",
  api: "API",
  noValue: "—",
} as const;

export const USER_MENU_TEXTS = {
  account: "Tu cuenta",
  accountOf: (name: string) => `Tu cuenta: ${name}`,
  role: "Rol",
  roles: "Roles",
} as const;

export const SIDEBAR_TEXTS = {
  newConversation: "Conversación nueva",
  chats: "Chats",
  memory: "Memoria",
  historyFailed: "No se pudo cargar el historial.",
  noConversations:
    "Todavía no hay conversaciones. La que empieces aparecerá aquí.",
  oneMessage: "1 mensaje",
  messages: (count: number) => `${String(count)} mensajes`,
  yesterday: "ayer",
} as const;

export const CONVERSATION_TEXTS = {
  actionsOf: (name: string) => `Acciones de «${name}»`,
  rename: "Renombrar",
  delete: "Borrar",
  renameTitle: "Renombrar la conversación",
  renameHint:
    "El nombre es solo para encontrarla en el historial; lo que se habló no cambia.",
  name: "Nombre",
  cancel: "Cancelar",
  save: "Guardar",
  saving: "Guardando…",
  deleteTitle: (name: string) => `¿Borrar «${name}»?`,
  deleteHint:
    "Se borra la conversación entera, con sus mensajes y con los archivos que se adjuntaron en ella. No se puede deshacer.",
  deleting: "Borrando…",
} as const;

export const MEMORY_TEXTS = {
  notes: "Notas",
  loadFailed: "No se pudo cargar la memoria.",
  profile: "Tu perfil",
  empty:
    "Todavía no hay nada guardado. El asistente irá anotando cómo trabajas a medida que trabajen juntos.",
  omitted: (count: number) =>
    `Hay ${String(count)} notas más que no caben en el índice que ve el asistente; las encuentra buscando.`,
  scopeClient: "cliente",
  back: "Volver",
  summary: "Resumen (es lo único que ve en el índice)",
  content: "Contenido",
  originManual: "Editada a mano.",
  originExplicit: "La guardó el asistente porque se lo pediste.",
  originInferred: "La dedujo el asistente de una conversación.",
  save: "Guardar",
  forget: "Olvidar",
} as const;

/** Los dos modos y las secciones del panel. */
export const NAV_TEXTS = {
  chat: "Chat",
  work: "Work",
  documents: "Facturas",
  usage: "Consumo",
  alfred: "Alfred",
  /* Las 7 pantallas del admin viejo (B2), en su mismo orden de menú. */
  participants: "Participantes",
  distributors: "Distribuidores",
  objectives: "Objetivos",
  periods: "Periodos",
  skus: "SKU",
  tasks: "Tareas",
  settings: "Ajustes",
} as const;
